import { useMutation, useQuery } from '@apollo/client';
import type { UpdateQueryFn } from '@apollo/client/core/watchQueryOptions';
import classNames from 'classnames';
import _ from 'lodash';
import type { Op } from 'quill/core';
import Quill from 'quill/core';
import type QuillHeader from 'quill/formats/header';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import type { SetNonNullable } from 'type-fest';
import { useDebouncedCallback } from 'use-debounce';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import { useQuill } from '../editor/QuillContext';
import { getRangeSaver } from '../editor/RangeSaver';
import { useUIElementResizeListener } from '../editor/UIElementResizeContext';
import { useWiderLineSize } from '../editor/WiderLineSizeContext';
import MentionContext from '../editor/controls/MentionContext';
import type { MagneticWallFocus } from '../editor/editorSlice';
import { CREATION_ID, EditorActions } from '../editor/editorSlice';
import AnnotationAttributor from '../editor/formats/Annotation';
import Comment from '../editor/formats/Comment';
import Reaction from '../editor/formats/Reaction';
import {
  getAttributorType,
  getClosestAttribute,
} from '../editor/formats/utils/attributors';
import getRangeBoundaries from '../editor/utils/getRangeBoundaries';
import COMMENT_REACTION_CREATED from '../graphql/commentReactionCreated';
import COMMENT_REACTION_DELETED from '../graphql/commentReactionDeleted';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import type { PostCommentThreadsQuery } from '../graphql/generated/graphql';
import THREAD_UNRESOLVED_SUBSCRIPTION from '../graphql/threadUnresolved';
import UNRESOLVE_THREAD_MUTATION from '../graphql/unresolveThread';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import addTestId from '../utils/addTestId';
import updateApolloList from '../utils/graphql/updateApolloList';
import { legacyUpdateQueryList } from '../utils/graphql/updateQueryList';
import updateSubscribedList from '../utils/graphql/updateSubscribedList';
import { getAnnotationIdFromUrlHash } from '../utils/url/annotationThreadHash';
import { getThreadIdFromUrlHash } from '../utils/url/commentThreadHash';
import { getReactionIdFromUrlHash } from '../utils/url/reactionThreadHash';
import useIsPublicView from '../utils/useIsPublicView';
import type { MagneticWallItemSize } from './MagneticWallItem';
import { usePostLayoutApi, usePostLayoutState } from './PostLayoutProvider';
import Annotation from './annotation/Annotation';
import AnnotationSheet from './annotation/AnnotationSheet';
import CommentThread from './comment/CommentThread';
import { MINIMIZED_COMMENT_HEIGHT } from './comment/CommentThreadMinimized';
import CommentThreadSheet from './comment/CommentThreadSheet';
import ReactionThread from './comment/ReactionThread';
import ReactionThreadSheet from './comment/ReactionThreadSheet';
import showFullPageSheet from './comment/showFullPageSheet';
import type {
  ContentRects,
  Heights,
  WiderLineRange,
} from './comment/usePositionsWithoutOverlaps';
import usePositionsWithoutOverlaps, {
  THREAD_SPACING,
} from './comment/usePositionsWithoutOverlaps';
import useMagneticWallItemSize from './useMagneticWallItemSize';
import { MainEditorResizeHandler } from './useOnMainEditorResize';
import useVisibleMagneticWallItems from './useVisibleMagneticWallItems';
import type { DisplayId } from './utils/toDisplayId';
import toDisplayId from './utils/toDisplayId';
import { getHeaderCollapse } from '../editor/useHeaderCollapse';

const POST_FRAGMENT = gql(`
  fragment MagneticWall_post on PublicPostFields {
    id
    ...CommentThread_post
    ...ReactionThread_post
    ...CommentThreadSheet_post
    ...Annotation_post
  }
`);

const MAGNETIC_WALL_THREADS_FRAGMENT = gql(`
  fragment MagneticWall_threads on PublicThreadFields {
    id
    ...CommentThread_thread
    ...ReactionThread_thread
    ...CommentThreadSheet_thread
    ...ReactionThreadSheet_thread
  }
`);

const MAGNETIC_WALL_ANNOTATIONS_FRAGMENT = gql(`
  fragment MagneticWall_annotations on PublicAnnotationFields {
    id
    ...Annotation_annotation
  }
`);

export const POST_THREADS_QUERY = gql(`
  query postCommentThreads($postId: ID!) {
    post(id: $postId) {
      id
      threads {
        id
        ...MagneticWall_threads
      }
      annotations {
        ...MagneticWall_annotations
      }
    }
  }
`);

const PUBLIC_POST_THREADS_QUERY = gql(`
  query publicPostCommentThreads($postId: ID!) {
    post: publicPost(id: $postId) {
      id
      threads {
        id
        ...MagneticWall_threads
      }
      annotations {
        ...MagneticWall_annotations
      }
    }
  }
`);

const CREATE_REACTION_MUTATION = gql(`
  mutation createReaction(
    $postId: ID!
    $version: Int!
    $checksum: Int!
    $mark: Json!
    $emoji: String!
  ) {
    createReaction(
      postId: $postId
      version: $version
      checksum: $checksum
      mark: $mark
      emoji: $emoji
    ) {
      ...MagneticWall_threads
    }
  }
`);

const ANNOTATION_CREATED_SUBSCRIPTION = gql(`
  subscription annotationCreated($postId: ID!) {
    annotation: annotationCreated(postId: $postId) {
      ...MagneticWall_annotations
    }
  }
`);

const ANNOTATION_DELETED_SUBSCRIPTION = gql(`
  subscription annotationDeleted($postId: ID!) {
    annotation: annotationDeleted(postId: $postId) {
      id
    }
  }
`);

const ANNOTATION_UPDATED_SUBSCRIPTION = gql(`
  subscription annotationUpdated($postId: ID!) {
    annotation: annotationUpdated(postId: $postId) {
      ...MagneticWall_annotations
    }
  }
`);

const THREAD_CREATED_SUBSCRIPTION = gql(`
  subscription threadCreated($postId: ID!) {
    thread: threadCreated(postId: $postId) {
      ...MagneticWall_threads
    }
  }
`);

const THREAD_DELETED_SUBSCRIPTION = gql(`
  subscription threadDeleted($postId: ID!) {
    thread: threadDeleted(postId: $postId) {
      id
    }
  }
`);

const THREAD_RESOLVED_SUBSCRIPTION = gql(`
  subscription threadResolved($postId: ID!) {
    thread: threadResolved(postId: $postId) {
      id
    }
  }
`);

// eslint-disable-next-line slab-graphql/no-unused-colocated-fragments
const THREAD_REPLIED_SUBSCRIPTION = gql(`
  subscription threadReplied($postId: ID!) {
    comment: threadReplied(postId: $postId) {
      ...CommentFields
      thread {
        id
      }
    }
  }
`);

// eslint-disable-next-line slab-graphql/no-unused-colocated-fragments
const THREAD_REACTED_SUBSCRIPTION = gql(`
  subscription threadReacted($postId: ID!) {
    reaction: threadReacted(postId: $postId) {
      ...ReactionFields
      thread {
        id
      }
    }
  }
`);

const COMMENT_DELETED_SUBSCRIPTION = gql(`
  subscription commentDeleted($postId: ID!) {
    comment: commentDeleted(postId: $postId) {
      id
      thread {
        id
      }
    }
  }
`);

const COMMENT_UPDATED_SUBSCRIPTION = gql(`
  subscription commentUpdated($postId: ID!) {
    comment: commentUpdated(postId: $postId) {
      ...CommentFields
      thread {
        id
      }
    }
  }
`);

const REACTION_DELETED_SUBSCRIPTION = gql(`
  subscription reactionDeleted($postId: ID!) {
    reaction: reactionDeleted(postId: $postId) {
      id
      thread {
        id
      }
    }
  }
`);

const updateWithinThread = (
  type: 'comment' | 'reaction',
  add: boolean,
): UpdateQueryFn<
  PostCommentThreadsQuery,
  { postId: string },
  {
    [x: string]:
      | {
          id?: string | null | undefined;
          thread: { id: string };
        }
      | null
      | undefined;
  }
> => {
  return (prev, { subscriptionData: { data } }) => {
    if (!prev || !data?.[type]?.thread) {
      return prev;
    }
    const types = `${type}s` as const;
    return {
      ...prev,
      post: {
        ...prev.post,
        threads: prev.post.threads.map((thread) => {
          if (thread.id !== data?.[type]?.thread.id) {
            return thread;
          }
          return updateApolloList(thread, data, types, type, add);
        }),
      },
    };
  };
};

type UpdateCommentSubscriptionData = {
  [x: string]:
    | {
        id?: string | null | undefined;
      }
    | null
    | undefined;
} & {
  commentReaction: {
    id?: string;
    threadId: string;
    commentId: string;
  };
};

const updateCommentReactions =
  (
    add: boolean,
  ): UpdateQueryFn<
    PostCommentThreadsQuery,
    { postId: string },
    UpdateCommentSubscriptionData
  > =>
  (prev, { subscriptionData: { data } }) =>
    !prev || !data || !data.commentReaction
      ? prev
      : {
          ...prev,
          post: prev.post && {
            ...prev.post,
            threads: prev.post.threads?.map((thread) =>
              thread.id !== data.commentReaction.threadId
                ? thread
                : {
                    ...thread,
                    comments: thread.comments?.map((comment) =>
                      comment.id !== data.commentReaction.commentId
                        ? comment
                        : updateApolloList(
                            comment,
                            data,
                            'reactions',
                            'commentReaction',
                            add,
                          ),
                    ),
                  },
            ),
          },
        };

const useMagneticWallItems = (
  postId: string,
  { subscribe } = { subscribe: false },
) => {
  const isPublicView = useIsPublicView();

  const { data, subscribeToMore } = useQuery(
    isPublicView ? PUBLIC_POST_THREADS_QUERY : POST_THREADS_QUERY,
    {
      variables: { postId },
    },
  );

  const subscribeThreads = useCallback(() => {
    const subscriptions = [
      subscribeToMore({
        document: THREAD_CREATED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: updateSubscribedList('post.threads', 'thread', true),
      }),
      subscribeToMore({
        document: THREAD_DELETED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: updateSubscribedList('post.threads', 'thread', false),
      }),
      subscribeToMore({
        document: THREAD_UNRESOLVED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: updateSubscribedList('post.threads', 'thread', true),
      }),
      subscribeToMore({
        document: THREAD_RESOLVED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: updateSubscribedList('post.threads', 'thread', false),
      }),
      subscribeToMore({
        document: ANNOTATION_CREATED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: updateSubscribedList(
          'post.annotations',
          'annotation',
          true,
        ),
      }),
      subscribeToMore({
        document: ANNOTATION_DELETED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: updateSubscribedList(
          'post.annotations',
          'annotation',
          false,
        ),
      }),
      subscribeToMore({
        document: ANNOTATION_UPDATED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: updateSubscribedList(
          'post.annotations',
          'annotation',
          true,
        ),
      }),
      subscribeToMore({
        document: THREAD_REPLIED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: updateWithinThread('comment', true),
      }),
      subscribeToMore({
        document: THREAD_REACTED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: updateWithinThread('reaction', true),
      }),
      subscribeToMore({
        document: COMMENT_DELETED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: updateWithinThread('comment', false),
      }),
      subscribeToMore({
        document: COMMENT_UPDATED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: updateWithinThread('comment', true),
      }),
      subscribeToMore({
        document: REACTION_DELETED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: updateWithinThread('reaction', false),
      }),
      subscribeToMore({
        document: COMMENT_REACTION_CREATED,
        variables: { postId },
        updateQuery: updateCommentReactions(true),
      }),
      subscribeToMore({
        document: COMMENT_REACTION_DELETED,
        variables: { postId },
        updateQuery: updateCommentReactions(false),
      }),
    ];

    return () => {
      subscriptions.forEach((unsubscribe) => {
        unsubscribe();
      });
    };
  }, [postId, subscribeToMore]);

  useEffect(() => {
    if (subscribe) {
      return subscribeThreads();
    }

    return undefined;
  }, [subscribe, subscribeThreads]);

  return useMemo<{
    threads: Array<
      | DocumentType<typeof POST_THREADS_QUERY>['post']['threads'][number]
      | DocumentType<
          typeof PUBLIC_POST_THREADS_QUERY
        >['post']['threads'][number]
    >;
    annotations: Array<
      | DocumentType<typeof POST_THREADS_QUERY>['post']['annotations'][number]
      | DocumentType<
          typeof PUBLIC_POST_THREADS_QUERY
        >['post']['annotations'][number]
    >;
  }>(
    () => ({
      threads: data?.post?.threads ?? [],
      annotations: data?.post.annotations ?? [],
    }),
    [data],
  );
};

export const useCreateReaction = () => {
  const [createReaction] = useMutation(CREATE_REACTION_MUTATION);

  return (
    postId: string,
    version: number,
    checksum: number,
    mark: string,
    emoji: string,
  ) => {
    return createReaction({
      variables: { postId, version, checksum, mark, emoji },
      update: legacyUpdateQueryList(
        { query: POST_THREADS_QUERY, variables: { postId } },
        'post.threads',
        'createReaction',
      ),
    });
  };
};

interface MagneticWallItemContainerProps {
  children: React.ReactNode;
  focused: boolean;
  top: number;
  displayId: DisplayId;
}

const MagneticWallItemContainer = ({
  children,
  top,
  focused,
  displayId,
}: MagneticWallItemContainerProps) => {
  return (
    <div
      data-display-id={displayId}
      className={classNames('magnetic-wall-item-container', {
        'magnetic-wall-item-container-focused': focused,
      })}
      style={{ top }}
    >
      {children}
    </div>
  );
};

export type ResizeHandler = (opts?: { immediate?: boolean }) => void;

type MagneticWallDisplayItemWithPosition = MagneticWallDisplayItem & {
  position: ReturnType<typeof usePositionsWithoutOverlaps>[string];
};
interface BaseMagneticWallProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  magneticWallFocus: MagneticWallFocus | null;
  onResize: ResizeHandler;
  visibleMagneticWallDisplayItems: MagneticWallDisplayItemWithPosition[];
  focusedDisplayItem: MagneticWallDisplayItem | null;
  canEditPost: boolean;
  widerLineMargin: number;
  magneticWallItemSize: MagneticWallItemSize;
}

const BaseMagneticWall = ({
  post,
  magneticWallFocus,
  onResize,
  visibleMagneticWallDisplayItems,
  focusedDisplayItem,
  canEditPost,
  widerLineMargin,
  magneticWallItemSize,
}: BaseMagneticWallProps) => {
  const { canMagneticWallExpand } = usePostLayoutState();
  const { expandMagneticWall } = usePostLayoutApi();
  const dispatch = useAppDispatch();

  const { quill } = useQuill();

  const handleBlur = () => {
    dispatch(EditorActions.blurMagneticWall());
  };

  const [unsavedInputValue, setUnsavedInputValue] = useState<
    Record<DisplayId, Op[]>
  >({});

  const { callback: debouncedHandleInputChange } = useDebouncedCallback(
    (displayId: DisplayId, value: Op[]) =>
      setUnsavedInputValue((unsaved) => ({ ...unsaved, [displayId]: value })),
    500,
  );

  const isShowingFullSheetItem = showFullPageSheet();

  const [unresolveThreadMutation] = useMutation(UNRESOLVE_THREAD_MUTATION);

  const unresolveThread = async (threadId: string) => {
    await unresolveThreadMutation({
      variables: { id: threadId },
      update: legacyUpdateQueryList(
        { query: POST_THREADS_QUERY, variables: { postId: post.id } },
        'post.threads',
        'unresolveThread',
        true,
      ),
    });
    expandMagneticWall();
  };

  const mentionContext = useMemo(() => ({ postId: post.id }), [post.id]);
  const shouldShowFullPageSheet = showFullPageSheet();

  const sortedMagneticWallDisplayItems = _.sortBy(
    visibleMagneticWallDisplayItems,
    (item) => -item.position.top,
  );

  return (
    <MentionContext.Provider value={mentionContext}>
      <ClickOutsideHandler
        onClickOutside={(e) => {
          const { target } = e;

          if (
            isShowingFullSheetItem ||
            // The editor is handling the bluring by itself
            (target instanceof Element && quill?.root.contains(target))
          ) {
            return;
          }

          handleBlur();
        }}
      >
        {sortedMagneticWallDisplayItems.map((item) => {
          const {
            data: { id },
            displayId,
            type,
            position,
          } = item;

          const { top, isMinimized } = position;

          const isFocused =
            magneticWallFocus?.id === id &&
            (magneticWallFocus.type === type ||
              (focusedDisplayItem == null &&
                magneticWallFocus.type === 'comment'));

          const hasExpandIntent =
            isFocused &&
            magneticWallFocus.hasExpandIntent &&
            canMagneticWallExpand &&
            !isMinimized;

          const isPopOverOpen =
            isFocused && Boolean(magneticWallFocus.active) && !hasExpandIntent;

          const unsavedInputValueForItem = unsavedInputValue[displayId];

          return (
            <MagneticWallItemContainer
              key={displayId}
              displayId={displayId}
              focused={!shouldShowFullPageSheet && isFocused}
              top={top}
            >
              {item.type === 'annotation' && (
                <Annotation
                  size={magneticWallItemSize}
                  annotation={item.data}
                  post={post}
                  unsavedInputValue={unsavedInputValueForItem}
                  active={isFocused}
                  minimized={isMinimized}
                  popOverOpen={isPopOverOpen}
                  onResize={onResize}
                  onInputChange={(ops) => {
                    debouncedHandleInputChange(displayId, ops);
                    onResize();
                  }}
                  canEdit={canEditPost}
                  widerLineMargin={widerLineMargin}
                />
              )}
              {item.type === 'reaction' && (
                <ReactionThread
                  size={magneticWallItemSize}
                  thread={item.data}
                  isFocused={isFocused}
                  minimized={isMinimized}
                  isPopOverOpen={isPopOverOpen}
                  onResize={onResize}
                  widerLineMargin={widerLineMargin}
                  post={post}
                />
              )}
              {item.type === 'comment' && (
                <CommentThread
                  size={magneticWallItemSize}
                  thread={item.data}
                  post={post}
                  unsavedInputValue={unsavedInputValueForItem}
                  isFocused={isFocused}
                  minimized={isMinimized}
                  isPopOverOpen={isPopOverOpen}
                  onResize={onResize}
                  onInputChange={(ops) => {
                    debouncedHandleInputChange(displayId, ops);
                  }}
                  unresolveThread={unresolveThread}
                  widerLineMargin={widerLineMargin}
                />
              )}
            </MagneticWallItemContainer>
          );
        })}
      </ClickOutsideHandler>

      {isShowingFullSheetItem &&
        focusedDisplayItem &&
        magneticWallFocus?.active && (
          <>
            {focusedDisplayItem.type === 'comment' && (
              <CommentThreadSheet
                post={post}
                thread={focusedDisplayItem.data}
                onClose={() => dispatch(EditorActions.blurMagneticWall())}
                onInputChange={(ops) => {
                  debouncedHandleInputChange(focusedDisplayItem.displayId, ops);
                }}
                unsavedInputValue={
                  unsavedInputValue[focusedDisplayItem.displayId]
                }
              />
            )}
            {focusedDisplayItem.type === 'reaction' && (
              <ReactionThreadSheet
                thread={focusedDisplayItem.data}
                onClose={() => dispatch(EditorActions.blurMagneticWall())}
              />
            )}
            {focusedDisplayItem.type === 'annotation' && (
              <AnnotationSheet
                postId={post.id}
                annotation={focusedDisplayItem.data}
                onClose={() => dispatch(EditorActions.blurMagneticWall())}
                onInputChange={(ops) =>
                  debouncedHandleInputChange(focusedDisplayItem.displayId, ops)
                }
                canEdit={canEditPost}
                unsavedInputValue={
                  unsavedInputValue[focusedDisplayItem.displayId] ?? []
                }
              />
            )}
          </>
        )}
    </MentionContext.Provider>
  );
};

const getReversedPrecedingHeaders = (
  node: HTMLElement,
  headers: QuillHeader[],
) =>
  headers
    .filter(
      (header) =>
        node.compareDocumentPosition(header.domNode) & // eslint-disable-line no-bitwise
        Node.DOCUMENT_POSITION_PRECEDING,
    )
    .toReversed();

function getRelativeContentRect(
  element: HTMLElement,
  container: HTMLElement,
): MagneticWallContentRect {
  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  return {
    left: elementRect.left - containerRect.left,
    top: elementRect.top - containerRect.top,
    bottom: elementRect.bottom - containerRect.top,
  };
}

const getContentRect = (
  contentNode: HTMLElement,
  container: HTMLElement,
  getVisibleCollapsedHeaders: () => QuillHeader[],
): MagneticWallContentRect => {
  const isWithinCollapsedHeader = !contentNode.offsetParent;

  if (isWithinCollapsedHeader) {
    const visibleCollapsedHeaders = getVisibleCollapsedHeaders();

    const header = getReversedPrecedingHeaders(
      contentNode,
      visibleCollapsedHeaders,
    )[0];

    if (header) {
      return getRelativeContentRect(header.domNode, container);
    }
  }

  return getRelativeContentRect(contentNode, container);
};

const getMinHeight = (
  adjustedTops: Record<string, { top: number; isMinimized: boolean }>,
  heights: Record<string, number | null>,
) => {
  const lowestItem = _.maxBy(
    _.map(adjustedTops, (value, threadId) => ({ ...value, threadId })),
    'top',
  );

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
  if (!lowestItem || !lowestItem.top) return 0;

  const { top, isMinimized, threadId } = lowestItem;
  return (
    top + (isMinimized ? MINIMIZED_COMMENT_HEIGHT : heights[threadId] || 0)
  );
};

export type MagneticWallContentRect = {
  top: number;
  left: number;
  bottom: number;
};

interface MagneticWallProps {
  quill: Quill;
  post: DocumentType<typeof POST_FRAGMENT>;
  canEditPost: boolean;
}

function queryNodesWithPrefixedIds({
  container,
  mapIds,
  prefix,
}: {
  container: HTMLElement;
  mapIds(node: HTMLElement): string[];
  prefix: string;
}) {
  const selector = `[class^="${prefix}"], [class*=" ${prefix}"]`;

  return Array.from(container.querySelectorAll<HTMLElement>(selector))
    .map((node) => ({ node, ids: mapIds(node) }))
    .filter(({ ids }) => ids.length);
}

export function getMagneticWallItemElementsWithIds(container: HTMLElement) {
  const comments = queryNodesWithPrefixedIds({
    container,
    mapIds: (node) => Comment.value(node) ?? [],
    prefix: `${Comment.keyName}-`,
  });

  const reactions = queryNodesWithPrefixedIds({
    container,
    mapIds: (node) => Reaction.value(node) ?? [],
    prefix: `${Reaction.keyName}-`,
  });

  const annotations = queryNodesWithPrefixedIds({
    container,
    mapIds: (node) => AnnotationAttributor.value(node) ?? [],
    prefix: `${AnnotationAttributor.keyName}-`,
  });

  return [...comments, ...annotations, ...reactions];
}

export function getContentRects(
  container: HTMLElement,
  getVisibleCollapsedHeaders: () => QuillHeader[],
): ContentRects {
  return getMagneticWallItemElementsWithIds(container)
    .flatMap(({ node, ids }) => {
      const contentRect = getContentRect(
        node,
        container,
        getVisibleCollapsedHeaders,
      );
      return ids.map((id) => ({ id, contentRect }));
    })
    .reduce((memo, { id, contentRect }) => {
      const existing = memo[id];
      if (existing != null) {
        return {
          ...memo,
          [id]: {
            ...existing,
            top: Math.min(contentRect.top, existing.top),
            bottom: Math.max(contentRect.bottom, existing.bottom),
          },
        };
      }
      return {
        ...memo,
        [id]: contentRect,
      };
    }, {} as ContentRects);
}

export type MagneticWallDisplayItem = { displayId: DisplayId } & (
  | {
      type: 'annotation';
      data: DocumentType<typeof MAGNETIC_WALL_ANNOTATIONS_FRAGMENT>;
    }
  | {
      type: 'reaction' | 'comment';
      data: DocumentType<typeof MAGNETIC_WALL_THREADS_FRAGMENT>;
    }
);

const useSelectionChangeHandler = (handler: (range: Range) => void) => {
  const lastRangeRef = useRef<Range | null>(null);
  const handlerRef = useRef<(range: Range) => void>(handler);
  useLayoutEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    const listener = () => {
      const selection = document.getSelection();
      if (!selection?.rangeCount) return;

      const range = selection.getRangeAt(0);
      const lastRange = lastRangeRef.current;
      if (
        lastRange &&
        lastRange.startContainer === range.startContainer &&
        lastRange.startOffset === range.startOffset &&
        lastRange.endContainer === range.endContainer &&
        lastRange.endOffset === range.endOffset
      ) {
        return;
      }
      lastRangeRef.current = range;
      handlerRef.current(range);
    };
    document.addEventListener('selectionchange', listener);
    return () => {
      document.removeEventListener('selectionchange', listener);
    };
  }, []);
};

type HashParseResult = {
  type: 'thread' | 'annotation' | 'reaction';
  id: string;
};

function parseHash(hash: string): HashParseResult | null {
  const threadId = getThreadIdFromUrlHash(hash);
  if (threadId) {
    return { type: 'thread', id: threadId };
  }

  const annotationId = getAnnotationIdFromUrlHash(hash);
  if (annotationId) {
    return { type: 'annotation', id: annotationId };
  }

  const reactionId = getReactionIdFromUrlHash(hash);
  if (reactionId) {
    return { type: 'reaction', id: reactionId };
  }

  return null;
}

function useScrollToThreadAnchor({ skip }: { skip: boolean }) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { hash } = location;

  useEffect(() => {
    if (skip) return;

    const parseResult = parseHash(hash);
    if (!parseResult) return;

    switch (parseResult.type) {
      case 'thread':
        dispatch(
          EditorActions.focusComment({
            id: parseResult.id,
            isActive: true,
            hasExpandIntent: true,
          }),
        );
        break;
      case 'annotation':
        dispatch(
          EditorActions.focusAnnotation({
            id: parseResult.id,
            isActive: true,
            hasExpandIntent: false,
          }),
        );
        break;
      case 'reaction':
        dispatch(
          EditorActions.focusReaction({
            id: parseResult.id,
            isActive: true,
            hasExpandIntent: false,
          }),
        );
        break;
      default:
        break;
    }

    const displayId: DisplayId =
      parseResult.type === 'thread'
        ? `comment-${parseResult.id}`
        : `${parseResult.type}-${parseResult.id}`;
    const selector = `[data-display-id="${displayId}"]`;

    const containerElement =
      document
        .querySelector(selector)
        ?.querySelector(
          '.magnetic-wall-item-full-container, .magnetic-wall-item-compact-container',
        ) ?? document.querySelector(selector);

    if (containerElement instanceof HTMLElement) {
      containerElement.scrollIntoView();
    }
  }, [dispatch, skip, hash]);
}

const MagneticWall = ({ quill, post, canEditPost }: MagneticWallProps) => {
  const magneticWallFocus = useAppSelector(
    (state) => state.editor.magneticWallFocus,
  );

  const currentUser = useAppSelector((state) => state.session.user);

  const { threads, annotations } = useMagneticWallItems(post.id, {
    subscribe: true,
  });

  const magneticWallDisplayItems: MagneticWallDisplayItem[] = useMemo(() => {
    const isCreating = magneticWallFocus?.id === CREATION_ID;
    const reactions = threads.filter((thread) => thread.reactions.length > 0);
    const commmentThreads = threads.filter(
      (thread) => thread.comments.length > 0,
    );

    return [
      ...annotations.map((data) => ({
        type: 'annotation' as const,
        data,
      })),
      ...commmentThreads.map((data) => ({
        type: 'comment' as const,
        data: { ...data, reactions: [] },
      })),
      ...reactions.map((data) => ({
        type: 'reaction' as const,
        data: { ...data, comments: [] },
      })),
      ...(isCreating
        ? [
            magneticWallFocus.type === 'annotation'
              ? {
                  data: {
                    __typename: 'Annotation' as const,
                    id: CREATION_ID,
                    author: currentUser,
                    content: [],
                  },
                  type: magneticWallFocus.type,
                }
              : {
                  data: {
                    __typename: 'Thread' as const,
                    id: CREATION_ID,
                    author: currentUser,
                    comments: [],
                    reactions: [],
                    content: [],
                  },
                  type: magneticWallFocus.type,
                },
          ]
        : []),
    ].map((item) => ({
      ...item,
      displayId: toDisplayId({ id: item.data.id, type: item.type }),
    }));
  }, [annotations, currentUser, magneticWallFocus, threads]);

  const dispatch = useAppDispatch();
  const addUIElementResizeListener = useUIElementResizeListener();

  const containerRef = useRef<HTMLDivElement>(null);

  const [contentRects, setContentRects] = useState<ContentRects>({});
  const [heights, setHeights] = useState<Heights>({});
  const [lastHeightOfFocusedThread, setLastHeightOfFocusedThread] = useState<{
    id: DisplayId | null;
    height: number | null;
  }>({
    id: null,
    height: 0,
  });
  const [widerLineRanges, setWiderLineRanges] = useState<WiderLineRange[]>([]);

  const magneticWallItemSize = useMagneticWallItemSize(containerRef);
  const positions = usePositionsWithoutOverlaps({
    heights,
    magneticWallItems: magneticWallDisplayItems,
    widerLineRanges,
    contentRects,
    magneticWallItemSize,
  });

  const visibleMagneticWallDisplayItems = useMemo(
    () =>
      magneticWallDisplayItems
        .map((item) => ({ ...item, position: positions[item.displayId] }))
        .filter(
          (item): item is SetNonNullable<typeof item, 'position'> =>
            item.position != null,
        ),
    [magneticWallDisplayItems, positions],
  );

  const visibleDisplayIds = useMemo(
    () =>
      new Set(visibleMagneticWallDisplayItems.map((item) => item.displayId)),
    [visibleMagneticWallDisplayItems],
  );

  useVisibleMagneticWallItems(visibleDisplayIds);

  const lastFocusedThreadId = lastHeightOfFocusedThread.id;
  const magneticWallFocusId = magneticWallFocus
    ? toDisplayId(magneticWallFocus)
    : null;

  if (magneticWallFocusId !== lastFocusedThreadId) {
    const focusedThreadHeight = magneticWallFocusId
      ? heights[magneticWallFocusId]
      : null;

    const newLastHeightOfFocusedThread = magneticWallFocusId
      ? { id: magneticWallFocusId, height: focusedThreadHeight ?? null }
      : { id: null, height: 0 };

    if (lastFocusedThreadId) {
      setHeights((h) => ({
        ...h,
        [lastFocusedThreadId]: lastHeightOfFocusedThread.height,
      }));
    }

    setLastHeightOfFocusedThread(newLastHeightOfFocusedThread);
  }

  const recalculateHeights = useCallback(() => {
    if (!magneticWallDisplayItems.length) return;

    const newHeights = magneticWallDisplayItems.reduce<Heights>((acc, item) => {
      const node = document.querySelector(
        `[data-display-id="${item.displayId}"]`,
      );

      return {
        ...acc,
        [item.displayId]:
          node instanceof HTMLElement ? node.offsetHeight : null,
      };
    }, {});

    setHeights((h) => ({ ...h, ...newHeights }));
  }, [magneticWallDisplayItems]);

  const { callback: debouncedHandleThreadResize } = useDebouncedCallback(
    recalculateHeights,
    100,
  );

  const { callback: debouncedUpdateDesiredPositions } = useDebouncedCallback(
    () => {
      if (!quill) return;
      const { container } = quill;

      const getVisibleCollapsedHeaders = _.memoize(() => {
        const collapsedHeaders =
          getHeaderCollapse(quill)?.getCollapsedHeaders() ?? [];
        return collapsedHeaders.filter(
          (header) => !!header.domNode.offsetParent,
        );
      });

      const newContentRects = getContentRects(
        container,
        getVisibleCollapsedHeaders,
      );

      if (magneticWallFocus?.id === CREATION_ID) {
        const boundaries = getRangeBoundaries(
          quill,
          getRangeSaver(quill).getLast(),
        );
        if (boundaries?.length) {
          const top = Math.min(...boundaries.map((rect) => rect.top));

          const createContentRect = {
            top,
            bottom: Math.max(
              ...boundaries.map((rect) => rect.top + rect.height),
            ),
            left: Math.min(...boundaries.map((rect) => rect.left)),
          };

          newContentRects[CREATION_ID] = createContentRect;
        }
      }

      const newWiderLineRanges = Array.from(
        container.querySelectorAll('.span-full'),
      ).flatMap((node) => {
        if (!(node as HTMLElement).offsetParent) return [];
        const contentRect = getContentRect(
          node as HTMLElement,
          container,
          getVisibleCollapsedHeaders,
        );

        return [contentRect];
      });

      setContentRects(newContentRects);
      setWiderLineRanges(newWiderLineRanges);
    },
    100,
  );

  useSelectionChangeHandler((range) => {
    let node = getLowestCommonAncestorContainer(range);
    if (!node) return;

    if (!quill.root.contains(node)) {
      // If the selection is inside the embed selection container.
      const normalized = quill.selection.normalizeNative(range) as {
        start: { node: Node };
      } | null;
      if (normalized) {
        // The end node should always be the same as the start node.
        node = normalized.start.node;
      } else {
        // Ignore when the selection is not inside the editor.
        return;
      }
    }
    const element = node instanceof Element ? node : node.parentElement;

    const closestAttribute = element && getClosestAttribute(element);

    if (!closestAttribute || quill.root.contains(closestAttribute.node)) {
      if (
        closestAttribute?.id &&
        magneticWallFocus?.id !== closestAttribute.id
      ) {
        const displayId = toDisplayId({
          id: closestAttribute.id,
          type: getAttributorType(closestAttribute.attributor),
        });
        const isVisible = visibleDisplayIds.has(displayId);
        if (!isVisible) return;

        if (closestAttribute.attributor === Comment) {
          dispatch(
            EditorActions.focusComment({
              id: closestAttribute.id,
              isActive: false,
              hasExpandIntent: false,
            }),
          );
        } else if (closestAttribute.attributor === Reaction) {
          dispatch(
            EditorActions.focusReaction({
              id: closestAttribute.id,
              isActive: false,
              hasExpandIntent: false,
            }),
          );
        } else if (closestAttribute.attributor === AnnotationAttributor) {
          dispatch(
            EditorActions.focusAnnotation({
              id: closestAttribute.id,
              isActive: false,
              hasExpandIntent: false,
            }),
          );
        }
      } else if (closestAttribute?.id == null && magneticWallFocus != null) {
        dispatch(EditorActions.blurMagneticWall());
      }
    }
  });

  useEffect(() => {
    quill.on(Quill.events.TEXT_CHANGE, debouncedUpdateDesiredPositions);

    const removeUIElementResizeListener: () => void =
      addUIElementResizeListener(debouncedUpdateDesiredPositions);
    const unsubscribeCollapseStateListener =
      getHeaderCollapse(quill)?.subscribeToLocalCollapseStateChange(
        debouncedUpdateDesiredPositions,
      ) ?? (() => {});

    debouncedUpdateDesiredPositions();

    return () => {
      quill.off(Quill.events.TEXT_CHANGE, debouncedUpdateDesiredPositions);

      removeUIElementResizeListener?.();
      unsubscribeCollapseStateListener();
    };
  }, [quill, debouncedUpdateDesiredPositions, addUIElementResizeListener]);

  useEffect(() => {
    debouncedHandleThreadResize();
  }, [threads, magneticWallFocus, debouncedHandleThreadResize]);

  const focusedDisplayItem =
    magneticWallFocus &&
    magneticWallDisplayItems.find(
      (item) =>
        item.data.id === magneticWallFocus.id &&
        item.type === magneticWallFocus.type,
    );

  const isShowingFullSheetItem =
    magneticWallFocus?.active &&
    focusedDisplayItem != null &&
    showFullPageSheet();

  useEffect(() => {
    if (!isShowingFullSheetItem) {
      debouncedUpdateDesiredPositions();
    }
  }, [
    debouncedUpdateDesiredPositions,
    isShowingFullSheetItem,
    magneticWallFocus,
  ]);

  const handleEditorContainerResize = () => {
    debouncedHandleThreadResize();
    debouncedUpdateDesiredPositions();
  };

  const handleResize = useCallback<ResizeHandler>(
    (opts) => {
      if (opts?.immediate) {
        recalculateHeights();
      } else {
        // this is the default to avoid unnecessary re-renders caused by the `unsavedInputValue` change on each keystroke.
        // The performance gains from this optimization are substantial because
        // `CommentThread` is a pretty large tree.
        debouncedHandleThreadResize();
      }
    },
    [debouncedHandleThreadResize, recalculateHeights],
  );

  useScrollToThreadAnchor({
    skip: Object.keys(positions).length === 0,
  });

  const { rightOverflowSize } = useWiderLineSize();

  const widerLineMargin = useMemo(() => {
    if (!containerRef.current) return rightOverflowSize;

    const diff =
      containerRef.current.getBoundingClientRect().left -
      quill.root.getBoundingClientRect().right;

    return rightOverflowSize - diff;
  }, [rightOverflowSize, quill]);

  return (
    <MainEditorResizeHandler onMainEditorResize={handleEditorContainerResize}>
      <div
        ref={containerRef}
        className="magnetic-wall"
        {...addTestId('magneticWall')}
        style={{
          minHeight: getMinHeight(positions, heights) + THREAD_SPACING,
        }}
      >
        <BaseMagneticWall
          post={post}
          magneticWallFocus={magneticWallFocus}
          onResize={handleResize}
          visibleMagneticWallDisplayItems={visibleMagneticWallDisplayItems}
          focusedDisplayItem={focusedDisplayItem ?? null}
          canEditPost={canEditPost}
          widerLineMargin={widerLineMargin}
          magneticWallItemSize={magneticWallItemSize}
        />
      </div>
    </MainEditorResizeHandler>
  );
};

// https://bugzilla.mozilla.org/show_bug.cgi?id=208427
function isFirefoxRestrictedNode(node: Node) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    node.parentNode;
  } catch (err) {
    return true;
  }

  return false;
}

export function getLowestCommonAncestorContainer(
  originalRange: Range,
): Node | null {
  if (
    isFirefoxRestrictedNode(originalRange.startContainer) ||
    isFirefoxRestrictedNode(originalRange.endContainer)
  ) {
    return null;
  }

  // To get the lowest common ancestor of the current range,
  // we narrow the range while keeping it visually identical.
  if (
    originalRange.collapsed ||
    originalRange.startContainer === originalRange.endContainer
  ) {
    // The range is already the narrowest one.
    return originalRange.commonAncestorContainer;
  }

  const range = originalRange.cloneRange();

  let sc: Node | null = range.startContainer;
  if (
    sc.nodeType === Node.TEXT_NODE &&
    range.startOffset === sc.nodeValue?.length
  ) {
    while (sc && !sc.nextSibling) {
      sc = sc.parentNode;
    }
    if (sc?.nextSibling) {
      range.setStart(sc.nextSibling, 0);
    }
  }

  if (range.endOffset === 0) {
    let ec: Node | null = range.endContainer;
    while (ec && !ec.previousSibling) {
      ec = ec.parentNode;
    }
    if (ec?.previousSibling) {
      const offset =
        ec.previousSibling.nodeType === Node.TEXT_NODE
          ? ec.previousSibling.nodeValue?.length
          : ec.previousSibling.childNodes?.length;
      if (typeof offset === 'number') {
        range.setEnd(ec.previousSibling, offset);
      }
    }
  }

  return range.commonAncestorContainer;
}

export default MagneticWall;
